/**
 * Deciding when a shopfront offers itself.
 *
 * Proximity alone isn't enough: on a 13 m concourse you are always within a few
 * metres of something, so a distance-only test pops a card at every bay you walk
 * past. What actually signals interest is looking at a shopfront — so the card
 * needs the shop squarely in your view, not merely somewhere nearby.
 *
 * Looking isn't enough either: standing mid-concourse and glancing down the
 * full 13 m width toward the far row satisfies "in view" from a long way off.
 * `maxDist` is the hard backstop — a straight-line cap so the card only ever
 * offers itself once you're actually close to the glass, however the
 * along/across box and the viewing angle shake out.
 *
 * "In view" is measured against the camera's real horizontal frustum rather than
 * a fixed angle. A hard-coded cone is wrong twice over: on a wide desktop window
 * it keeps a card alive for a shop that has already slid off the edge of the
 * screen, and on a portrait phone — where the horizontal field of view is a
 * third as wide — it fires for shops that were never visible at all. Tying the
 * rule to the frustum means "if you can see it" holds on every screen.
 *
 * The one exception is arm's length from the glass, where you've plainly
 * arrived whichever way your head happens to be pointing.
 *
 * Facing is measured on the horizontal only. Tilting up to read a fascia sign
 * shouldn't dismiss the shop you're standing in front of.
 *
 * Pure and free of three.js so the rules can be checked directly, without
 * standing a renderer up and trying to drive a camera through a browser.
 */

const DEG = Math.PI / 180

/** Camera state reduced to what the decision actually depends on. */
export interface ConcourseView {
  x: number
  z: number
  /** Radians; 0 looks down −Z, positive turns toward −X. */
  yaw: number
  /** Half the camera's horizontal field of view, radians. See horizontalHalfFov(). */
  halfFov: number
}

/** How a shopfront sits relative to the viewer. */
export interface ShopfrontBearing {
  /** Offset along the concourse from the bay centre, metres. */
  along: number
  /** Offset across the concourse from the shopfront line, metres. */
  across: number
  /** Straight-line distance on the floor plane, metres. */
  dist: number
  /** Horizontal angle between gaze and the shopfront, radians. 0 = dead centre. */
  angle: number
  /** Half the viewer's horizontal frustum, carried through for the gates. */
  halfFov: number
}

export const PROXIMITY = {
  show: {
    along: 2.6,
    across: 4.6,        // your own side, or just across the aisle — not the far row
    maxDist: 5,          // straight-line backstop: this close, whichever way you got there
    /** Fraction of the half-frustum the shop must sit within: the middle of the screen. */
    viewFraction: 0.5,
    minAngle: 16 * DEG, // floor, so a portrait phone doesn't demand pixel-perfect aim
    maxAngle: 30 * DEG, // ceiling, so an ultrawide window doesn't offer half the mall
    veryNear: 1.6,      // at the door; facing stops mattering
  },
  /** Looser than `show` throughout; without hysteresis the card strobes on the boundary. */
  hold: {
    along: 3.6,
    across: 5.8,
    maxDist: 6.5,
    /** Full half-frustum: the card lives exactly as long as the shop is on screen. */
    viewFraction: 1.0,
    minAngle: 30 * DEG,
    maxAngle: 55 * DEG,
    veryNear: 2.1,
  },
} as const

/**
 * Horizontal half-FOV from a perspective camera's vertical FOV and aspect.
 * three.js `PerspectiveCamera.fov` is the vertical angle, in degrees.
 */
export function horizontalHalfFov(verticalFovDeg: number, aspect: number): number {
  return Math.atan(Math.tan((verticalFovDeg * DEG) / 2) * Math.max(aspect, 0.05))
}

export function bearingTo(view: ConcourseView, slotX: number, slotZ: number): ShopfrontBearing {
  const dx = slotX - view.x
  const dz = slotZ - view.z
  const dist = Math.max(Math.hypot(dx, dz), 1e-4)
  const forwardX = -Math.sin(view.yaw)
  const forwardZ = -Math.cos(view.yaw)
  const facing = (dx * forwardX + dz * forwardZ) / dist
  return {
    along: Math.abs(dz),
    across: Math.abs(dx),
    dist,
    angle: Math.acos(Math.min(1, Math.max(-1, facing))),
    halfFov: view.halfFov,
  }
}

type Gate = typeof PROXIMITY.show | typeof PROXIMITY.hold

function passes(b: ShopfrontBearing, gate: Gate): boolean {
  const limit = Math.min(gate.maxAngle, Math.max(gate.minAngle, b.halfFov * gate.viewFraction))
  return b.along < gate.along
    && b.across < gate.across
    && b.dist < gate.maxDist
    && (b.angle <= limit || b.dist < gate.veryNear)
}

/** Should this shopfront offer itself to someone not currently being offered one? */
export function isOffered(b: ShopfrontBearing): boolean {
  return passes(b, PROXIMITY.show)
}

/** Should an already-visible card stay up? */
export function isHeld(b: ShopfrontBearing): boolean {
  return passes(b, PROXIMITY.hold)
}

/**
 * Ranking among offered shopfronts: closest and most squarely in view wins, so
 * turning between two adjacent bays picks the one you're facing. Lower is better.
 */
export function offerScore(b: ShopfrontBearing): number {
  return b.dist * (1 + b.angle)
}
